import {
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Link,
  Text,
  TextField,
  Toast,
  BlockStack,
  ExceptionList,
} from '@shopify/polaris';
import QuickUploadImage from 'components/oneclick-upload-image';
import { useCallback, useEffect, useState } from 'react';
import { StatusActiveIcon, AlertCircleIcon } from '@shopify/polaris-icons';

import helpers from 'helpers';
import dateandtime from 'date-and-time';
import __ from 'languages/index';
import { useUpdateUser } from 'queries/users.query';

export default function UserGeneralInformation({ entity }) {
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateUser();

  const [internalErrorNotice, setInternalErrorNotice] = useState('');
  const [srcImageAvatar, setSrcImageAvatar] = useState('');

  useEffect(() => {
    setSrcImageAvatar(entity?.user_avatar);
  }, [entity]);

  const handQuickUpdateSuccess = useCallback(
    async (res: any) => {
      setSrcImageAvatar(res.media_url);
      try {
        await updateEntity({
          user_id: entity?.user_id,
          user_avatar: res.media_url,
        });
      } catch (e) {}
    },
    [entity]
  );

  const handUploadError = useCallback((err: any) => {
    setInternalErrorNotice(err);
  }, []);

  /**
   * Quick note
   */
  const [showQuickNote, setShowQuickNote] = useState(false);
  const [biotext, setBiotext] = useState('');

  useEffect(() => {
    if (entity) {
      setBiotext(entity.bio || '');
    }
  }, [entity]);

  useEffect(() => {
    if (updateSuccess === true) {
      setShowQuickNote(false);
    }
  }, [updateSuccess]);

  const saveQuickNoteButtonCallback = useCallback(async () => {
    try {
      await updateEntity({
        user_id: entity?.user_id,
        bio: biotext,
      });
    } catch (e) {}
  }, [entity, biotext]);

  const handleQuickNoteChange = useCallback((newValue: string) => setBiotext(newValue), []);

  return (
    <>
      {internalErrorNotice && <Toast error content={internalErrorNotice} onDismiss={() => setInternalErrorNotice(null)} />}

      <InlineGrid columns={{ xs: '1', sm: '1', md: '1', lg: ['twoThirds', 'oneThird'] }} gap={'400'}>
        <Card>
          <InlineStack align="start" gap="400" blockAlign="center">
            <div>
              <QuickUploadImage onSuccess={handQuickUpdateSuccess} onError={handUploadError}>
                <Avatar name={entity?.display_name} customer source={helpers.getMediaLink(srcImageAvatar, srcImageAvatar)} size="md" />
              </QuickUploadImage>
            </div>

            <BlockStack gap="100">
              <Text as="h2" variant="headingLg" fontWeight="bold">
                {entity?.user_title && `(${__(entity?.user_title)})`} {entity?.display_name || entity?.user_login}
              </Text>
              <InlineStack gap={'200'}>
                <Text as="p" variant="bodySm" tone="subdued">
                  <Badge>{entity?.user_role}</Badge>
                </Text>
              </InlineStack>
            </BlockStack>
          </InlineStack>

          <br />
          <Divider />
          <br />

          <BlockStack gap={'400'}>
            {entity?.user_birthday && (
              <div>
                <Text variant="bodyMd" as="p" fontWeight="bold">
                  Sinh nhật
                </Text>
                <Text as="p">
                  {dateandtime.format(new Date(entity?.user_birthday), 'DD-MM-YYYY')} {'  -  '}{' '}
                  {helpers.getAge(new Date(entity?.user_birthday), ' tuổi')}
                </Text>
              </div>
            )}
            <div>
              <Text variant="bodyMd" as="p" fontWeight="bold">
                Bio
              </Text>
              {showQuickNote ? (
                <>
                  <TextField
                    label=""
                    multiline={2}
                    value={biotext}
                    onChange={handleQuickNoteChange}
                    autoComplete="off"
                    maxLength={250}
                    showCharacterCount
                  />
                  <Button variant="plain" onClick={saveQuickNoteButtonCallback} loading={updating}>
                    Save
                  </Button>
                  &nbsp; or &nbsp;
                  <Button
                    variant="plain"
                    onClick={() => {
                      setShowQuickNote(false);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Link removeUnderline monochrome onClick={() => setShowQuickNote(true)}>
                  {entity?.bio ?? 'Chưa có giới thiệu bản thân'}
                </Link>
              )}
            </div>
          </BlockStack>
        </Card>
        <Card>
          <Text as="h3" variant="headingMd">
            Thông tin liên hệ
          </Text>
          <BlockStack gap={'200'}>
            <Text variant="bodyMd" as="p" fontWeight="bold">
              Địa chỉ
            </Text>
            <Text as="p" tone="critical">
              {entity?.user_address ?? 'Chưa có thông tin'}
            </Text>
          </BlockStack>
          <br />
          <BlockStack gap={'200'}>
            <ExceptionList
              items={[
                {
                  title: 'Email',
                  status: 'warning',
                  icon: StatusActiveIcon,
                  description: entity?.user_email ? <Link url="#!">{entity?.user_email}</Link> : 'Chưa có',
                },

                {
                  title: 'Phone number',
                  status: 'warning',
                  icon: StatusActiveIcon,
                  description: entity.user_phonenumber ? <Link url={`tel:` + entity?.user_phonenumber}>{entity?.user_phonenumber}</Link> : 'Chưa có',
                },
              ]}
            />
          </BlockStack>
        </Card>
      </InlineGrid>
    </>
  );
}

